import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from 'motion/react';
import { processSteps } from '../../../data/site';
import { EASE_OUT } from '../../../lib/motion';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import Label from '../../primitives/Label';
import ScrambleText from '../../primitives/ScrambleText';
import SplitText from '../../primitives/SplitText';
import { useScrollTo } from '../../layout/SmoothScroll';

const total = processSteps.length;
const pad = n => String(n).padStart(2, '0');

// Vertical circuit trace through the eight steps; `progress` (0–1) draws it and lights the pads
const Spine = ({ progress, active, onSelect }) => (
  <ol className="relative">
    <div className="absolute bottom-5 left-[7px] top-5 w-px bg-foreground/15" aria-hidden="true">
      <motion.div
        className="absolute inset-x-[-0.5px] top-0 h-full origin-top bg-gradient-to-b from-brand-2 to-brand shadow-[0_0_12px_hsl(var(--brand)/0.6)]"
        style={{ scaleY: progress }}
      />
    </div>
    {processSteps.map((step, i) => {
      const reached = i <= active;
      return (
        <li key={step.title}>
          <button
            type="button"
            onClick={() => onSelect(i)}
            className="group relative flex w-full items-center gap-5 py-[0.55rem] text-left"
            aria-current={i === active ? 'step' : undefined}
          >
            <span
              className={`relative z-10 size-[15px] shrink-0 rounded-full border transition-colors duration-500 ${
                reached ? 'border-brand bg-brand' : 'border-foreground/25 bg-background'
              }`}
            >
              {i === active && <span className="absolute -inset-1.5 animate-ping-soft rounded-full border border-brand/60 motion-reduce:animate-none" />}
            </span>
            <span className="label w-7 shrink-0 tabular">{pad(i + 1)}</span>
            <span
              className={`text-[clamp(1.05rem,1.5vw,1.375rem)] tracking-[-0.02em] transition-[color,filter,opacity] duration-500 [font-stretch:108%] ${
                i === active ? 'text-foreground' : 'text-foreground/35 blur-[0.6px] group-hover:text-foreground/70 group-hover:blur-0'
              }`}
            >
              {i === active ? <ScrambleText text={step.title} play={active} duration={520} /> : step.title}
            </span>
          </button>
        </li>
      );
    })}
  </ol>
);

// The active step in detail
const Detail = ({ index }) => {
  const step = processSteps[index];
  const Icon = step.icon;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 24, filter: 'blur(10px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={{ opacity: 0, y: -16, filter: 'blur(8px)' }}
        transition={{ duration: 0.55, ease: EASE_OUT }}
        className="flex flex-col"
      >
        <div className="flex items-center gap-4">
          <span className="flex size-12 items-center justify-center rounded-xl border border-foreground/10 text-brand">
            <Icon size={22} strokeWidth={1.5} aria-hidden="true" />
          </span>
          <Label index={pad(index + 1)}>{step.phase}</Label>
        </div>
        <h3 className="display-md mt-8">{step.title}</h3>
        <p className="lede mt-5 max-w-md">{step.description}</p>
      </motion.div>
    </AnimatePresence>
  );
};

/*
 * "Our Proven Process": on desktop the section pins for a few screens while a circuit trace
 * draws through the eight steps; each title decodes as the pulse reaches it. On small
 * screens (and with reduced motion) it is a plain list.
 */
const ProcessCircuit = ({ index = '06' }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const wide = useMediaQuery('(min-width: 1024px)');
  const pinned = wide && !reduced;
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.3 });
  const [active, setActive] = useState(0);
  const scrollTo = useScrollTo();

  useMotionValueEvent(scrollYProgress, 'change', v => {
    const next = Math.min(total - 1, Math.floor(v * total * 0.999));
    if (next !== active) setActive(next);
  });

  // Clicking a step scrolls to its slice of the pinned section
  const select = i => {
    if (!pinned) return setActive(i);
    const el = ref.current;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const span = el.offsetHeight - window.innerHeight;
    scrollTo(top + span * ((i + 0.5) / total));
  };

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <Label index={index}>Our proven process</Label>
        <SplitText as="h2" text="From concept *to launch*" className="display-lg mt-6" />
        <p className="lede mt-5 max-w-xl">A transparent, step-by-step journey from concept to launch</p>
      </div>
      <span className="font-mono text-sm tabular text-muted-foreground" aria-hidden="true">
        <span className="text-foreground">{pad(active + 1)}</span> / {pad(total)}
      </span>
    </div>
  );

  if (!pinned) {
    return (
      <section ref={ref} data-tone="ink" className="theme-ink section bg-background">
        <div className="container-site">
          {header}
          <ol className="mt-14 space-y-10 border-l border-foreground/15 pl-6">
            {processSteps.map((step, i) => (
              <li key={step.title} className="relative">
                <span className="absolute -left-[31px] top-1.5 size-[13px] rounded-full border border-brand bg-background" aria-hidden="true" />
                <Label index={pad(i + 1)}>{step.phase}</Label>
                <h3 className="title-lg mt-3">{step.title}</h3>
                <p className="mt-2 text-muted-foreground">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} data-tone="ink" className="theme-ink relative bg-background" style={{ height: `${total * 38 + 100}vh` }}>
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="blueprint pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_70%_60%_at_70%_55%,#000,transparent)]" aria-hidden="true" />
        <div className="container-site relative">
          {header}
          <div className="mt-[clamp(2rem,5vh,4rem)] grid grid-cols-12 gap-8">
            <div className="col-span-5">
              <Spine progress={progress} active={active} onSelect={select} />
            </div>
            <div className="col-span-6 col-start-7 flex items-center">
              <Detail index={active} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessCircuit;
